import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Tooltip,
  Divider,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import * as XLSX from "xlsx";
import styles from "./outcome-finalizer.module.scss";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { recordAgentAction } from "../../../store/agentSlice";
import MarkdownRenderer from "../../MarkdownRenderer";

interface ClauseReference {
  clause_number?: string;
  section_reference: string;
  clause_text_snippet: string;
  clause_document_url?: string;
}

interface ContractDetails {
  contract_type: string;
  contracting_parties: string[];
  agreement_date: string;
}

interface DocumentOutcome {
  document_name: string;
  contract_details: ContractDetails;
  is_coc_clause_present: boolean;
  clause_reference: ClauseReference[];
  extracted_threshold: string;
  transaction_percentage: string;
  trigger_result: string;
  reason: string;
  action_required: string | string[];
  consequence_summary: string;
  risk_tier: string;
  human_review: string;
  feedback_type?: string;
  feedback_comment?: string;
}

interface ParsedContent {
  finalAnswer?: {
    documents?: DocumentOutcome[];
  };
  question?: string;
}

interface OutcomeFinalizerAgentProps {
  parsedContent: ParsedContent;
}

interface ClauseModalData {
  documentName: string;
  clauses: ClauseReference[];
}

const getRiskTierColor = (riskTier: string): string => {
  const normalized = riskTier?.toLowerCase();
  if (normalized === "green" || normalized === "low") return "green";
  if (normalized === "red" || normalized === "high") return "red";
  if (normalized === "yellow" || normalized === "medium") return "medium";
  return "medium";
};

const formatContractingParties = (parties: string[]): string => {
  if (!parties || parties.length === 0) return "N/A";
  if (parties.length === 1) return parties[0];
  if (parties.length === 2) return `${parties[0]} and ${parties[1]}`;
  return `${parties.slice(0, -1).join(", ")}, and ${parties[parties.length - 1]}`;
};

export default function OutcomeFinalizerAgent({
  parsedContent,
}: OutcomeFinalizerAgentProps): JSX.Element {
  const dispatch = useAppDispatch();
  const currentAgent = useSelector(
    (state: RootState) => state.agents.currentAgent,
  );
  const numericAgentId = currentAgent?.id || 11;
  const documents = parsedContent?.finalAnswer?.documents || [];
  const [modalData, setModalData] = useState<ClauseModalData | null>(null);

  const handleClauseClick = (
    documentName: string,
    clauses: ClauseReference[],
  ) => {
    if (clauses && clauses.length > 0) {
      setModalData({ documentName, clauses });
    }
  };

  const handleCloseModal = () => {
    setModalData(null);
  };

  const handleExportExcel = () => {
    try {
      // Prepare headers (matching table columns exactly)
      const headers = [
        "Contract Details",
        "Identify CoC",
        "Clause Reference",
        "Identify CoC Trigger",
        "Action Items",
        "Consequence of not adhering to CoC requirements",
        "Risk Tier",
        "Feedback",
      ];

      // Prepare data for Excel export
      const excelData: any[][] = [];

      // Add header row
      excelData.push(headers);

      // Add empty row after header
      excelData.push([]);

      documents.forEach((doc) => {
        // Format contract details (Type, Parties, Date)
        const contractDetails = doc.contract_details
          ? `Type: ${doc.contract_details.contract_type}\nParties: ${formatContractingParties(doc.contract_details.contracting_parties)}\nDate: ${doc.contract_details.agreement_date}`
          : doc.document_name;

        // Format clause references (numbers only for main row)
        const clauseRefs = doc.clause_reference
          ?.map((ref) => ref.clause_number)
          .filter(Boolean)
          .join(", ") || "N/A";

        // Format feedback
        const feedback = doc.feedback_type && doc.feedback_comment
          ? `Type: ${doc.feedback_type}\nComment: ${doc.feedback_comment}`
          : doc.feedback_type
            ? `Type: ${doc.feedback_type}`
            : doc.feedback_comment
              ? `Comment: ${doc.feedback_comment}`
              : "No feedback";

        // Main document row
        const mainRow = [
          contractDetails,
          doc.is_coc_clause_present ? "Yes" : "No",
          clauseRefs,
          doc.trigger_result || "N/A",
          Array.isArray(doc.action_required)
            ? doc.action_required.join("\n")
            : doc.action_required || "N/A",
          doc.consequence_summary || "N/A",
          doc.risk_tier || "N/A",
          feedback,
        ];

        excelData.push(mainRow);

        // Add clause detail rows if available
        if (doc.clause_reference && doc.clause_reference.length > 0) {
          doc.clause_reference.forEach((clause) => {
            const clauseDetailRow = [
              `  └─ Clause ${clause.clause_number || ""}`,
              "",
              `Section: ${clause.section_reference || "N/A"}\n\n"${clause.clause_text_snippet || "N/A"}"`,
              "",
              "",
              "",
              "",
              "",
            ];
            excelData.push(clauseDetailRow);
          });
        }

        // Add empty row for separation between documents
        excelData.push([]);
      });

      // Create worksheet from array of arrays
      const ws = XLSX.utils.aoa_to_sheet(excelData);

      // Set column widths for better readability (matching 8 table columns)
      const columnWidths = [
        { wch: 50 }, // Contract Details (Type, Parties, Date combined)
        { wch: 15 }, // Identify CoC
        { wch: 30 }, // Clause Reference
        { wch: 20 }, // Identify CoC Trigger
        { wch: 50 }, // Action Items
        { wch: 50 }, // Consequence of not adhering to CoC requirements
        { wch: 12 }, // Risk Tier
        { wch: 40 }, // Feedback (Type and Comment combined)
      ];
      ws["!cols"] = columnWidths;

      // Enable text wrapping for all cells
      const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          if (!ws[cellAddress]) continue;
          if (!ws[cellAddress].s) ws[cellAddress].s = {};
          ws[cellAddress].s.alignment = { wrapText: true, vertical: "top" };
        }
      }

      // Create workbook and add worksheet
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "CoC Analysis");

      // Generate file name
      const timestamp = new Date().toISOString().split("T")[0];
      const fileName = `coc-final-outcome-${timestamp}.xlsx`;

      // Record action
      dispatch(
        recordAgentAction({
          agentId: numericAgentId,
          actionType: "download",
          content: JSON.stringify({
            finalAnswer: {
              documents: documents,
            },
          }),
        }),
      );

      // Download file
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error("Error exporting Excel:", error);
    }
  };

  const handleDownloadPDF = () => {
    // Deprecated - replaced with Excel export
    handleExportExcel();
  };

  if (documents.length === 0) {
    return (
      <div className={styles.container}>
        <p>No outcome data available.</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerSection}>
        <h2 className={styles.title}>
          Change of Control Analysis - Final Report
        </h2>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Contract Details</th>
              <th>Identify CoC</th>
              <th>Clause Reference</th>
              <th>Identify CoC Trigger</th>
              <th>Action Items</th>
              <th>Consequence of not adhering to CoC requirements</th>
              <th>Risk Tier</th>
              <th>Feedback</th>
            </tr>
          </thead>
          <tbody>
            {documents.map((doc, index) => (
              <tr key={index}>
                <td className={styles.documentName}>
                  {doc.contract_details ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      <div>
                        <strong
                          style={{ color: "#00a888", fontSize: "0.85em" }}
                        >
                          Type:
                        </strong>{" "}
                        <span>{doc.contract_details.contract_type}</span>
                      </div>
                      <div>
                        <strong
                          style={{ color: "#00a888", fontSize: "0.85em" }}
                        >
                          Parties:
                        </strong>{" "}
                        <span>
                          {formatContractingParties(
                            doc.contract_details.contracting_parties,
                          )}
                        </span>
                      </div>
                      <div>
                        <strong
                          style={{ color: "#00a888", fontSize: "0.85em" }}
                        >
                          Date:
                        </strong>{" "}
                        <span>{doc.contract_details.agreement_date}</span>
                      </div>
                    </div>
                  ) : (
                    doc.document_name
                  )}
                </td>
                <td className={styles.centered}>
                  <span
                    className={
                      doc.is_coc_clause_present ? styles.yes : styles.no
                    }
                  >
                    {doc.is_coc_clause_present ? "Yes" : "No"}
                  </span>
                </td>
                <td className={styles.clauseReference}>
                  {doc.clause_reference && doc.clause_reference.length > 0 ? (
                    <span
                      className={styles.clickableClause}
                      onClick={() =>
                        handleClauseClick(
                          doc.document_name,
                          doc.clause_reference,
                        )
                      }
                      title="Click to view clause details"
                    >
                      {doc.clause_reference
                        .map((ref) => ref.clause_number)
                        .filter(Boolean)
                        .join(", ") || "View Details"}
                    </span>
                  ) : (
                    "N/A"
                  )}
                </td>
                <td className={styles.centered}>
                  <Tooltip title={doc.reason || "N/A"} arrow placement="top">
                    <span className={styles.triggerBadge}>
                      {doc.trigger_result}
                    </span>
                  </Tooltip>
                </td>
                <td className={styles.actionRequired}>
                  {Array.isArray(doc.action_required) ? (
                    <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
                      {doc.action_required.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    doc.action_required
                  )}
                </td>
                <td className={styles.actionRequired}>
                  {doc.consequence_summary || "N/A"}
                </td>
                <td className={styles.centered}>
                  <span
                    className={`${styles.riskBadge} ${
                      styles[getRiskTierColor(doc.risk_tier)]
                    }`}
                  ></span>
                </td>
                <td className={styles.feedbackCell}>
                  {doc.feedback_type || doc.feedback_comment ? (
                    <div className={styles.feedbackDisplay}>
                      {doc.feedback_type && (
                        <div className={styles.feedbackType}>
                          <strong>Type:</strong> {doc.feedback_type}
                        </div>
                      )}
                      {doc.feedback_comment && (
                        <div className={styles.feedbackComment}>
                          <strong>Comment:</strong> {doc.feedback_comment}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className={styles.noFeedback}>No feedback</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Clause Details Modal */}
      <Dialog
        open={Boolean(modalData)}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, pr: 6 }}>
          <Typography
            variant="h6"
            component="div"
            sx={{ fontWeight: 600, color: "#1a1a1a" }}
          >
            Clause References
          </Typography>
          {modalData?.documentName && (
            <Typography variant="body2" sx={{ color: "#666", mt: 0.5 }}>
              Document: {modalData.documentName}
            </Typography>
          )}
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "#666",
              "&:hover": { backgroundColor: "#f5f5f5" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {modalData?.clauses?.map((clause, index) => (
            <Box
              key={index}
              sx={{ mb: index < modalData.clauses.length - 1 ? 3 : 0 }}
            >
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <Box sx={{ flex: 1 }}>
                    {clause.clause_number && (
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: "#1a1a1a", mb: 0.5 }}
                      >
                        Clause {clause.clause_number}
                      </Typography>
                    )}
                    {clause.section_reference && (
                      <Typography
                        variant="body2"
                        sx={{ color: "#00a888", fontWeight: 500 }}
                      >
                        Section: {clause.section_reference}
                      </Typography>
                    )}
                  </Box>
                  {clause.clause_document_url && (
                    <Tooltip title="View source document" arrow>
                      <IconButton
                        size="small"
                        onClick={() => window.open(clause.clause_document_url, '_blank')}
                        sx={{
                          color: "#00a888",
                          "&:hover": { backgroundColor: "rgba(0, 168, 136, 0.08)" },
                        }}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
              {clause.clause_text_snippet && (
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#f9f9f9",
                    borderRadius: 1,
                    borderLeft: "3px solid #00a888",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#555",
                      fontStyle: "italic",
                      lineHeight: 1.6,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    "{clause.clause_text_snippet}"
                  </Typography>
                </Box>
              )}
              {index < modalData.clauses.length - 1 && (
                <Divider sx={{ mt: 3 }} />
              )}
            </Box>
          ))}
          {(!modalData?.clauses || modalData.clauses.length === 0) && (
            <Typography
              variant="body2"
              sx={{ color: "#999", textAlign: "center", py: 2 }}
            >
              No clause details available
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Export Section */}
      <div className={styles.downloadSection}>
        <div className={styles.downloadContent} style={{ cursor: "pointer" }}>
          <FileDownloadIcon className={styles.downloadSectionIcon} />
          <div className={styles.downloadText}>
            <Typography className={styles.downloadTitle}>
              Export to Excel
            </Typography>
            <Typography className={styles.downloadDescription}>
              Get a comprehensive Excel report of your Change of Control analysis
            </Typography>
          </div>
        </div>
        <Tooltip title="Export to Excel" arrow>
          <IconButton
            className={styles.downloadButton}
            onClick={handleExportExcel}
            aria-label="Export Excel Report"
          >
            <FileDownloadIcon className={styles.downloadButtonIcon} />
            <span>Export Excel</span>
          </IconButton>
        </Tooltip>
      </div>

      {/* Question Section */}
      {parsedContent?.question && (
        <Box className={styles.questionBox}>
          <Box className={styles.questionContent}>
            <MarkdownRenderer content={parsedContent.question} />
          </Box>
        </Box>
      )}
    </div>
  );
}
